// Маленький роутер на History API. Сервер отдаёт index.html на любые пути (SPA fallback).
import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react'

export interface NavState {
  /** Страница «под» шторкой задачи: откуда открыли задачу. */
  bg?: string
}

export interface Loc {
  pathname: string
  search: string
  hash: string
  state: NavState | null
}

function read(): Loc {
  const st = window.history.state as unknown
  return {
    pathname: window.location.pathname || '/',
    search: window.location.search,
    hash: window.location.hash,
    state: st && typeof st === 'object' ? (st as NavState) : null,
  }
}

let current: Loc = read()
const listeners = new Set<() => void>()

function emit(): void {
  current = read()
  for (const l of listeners) l()
}

window.addEventListener('popstate', emit)
window.addEventListener('hashchange', emit)

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function getLocation(): Loc {
  return current
}

export function useLocation(): Loc {
  return useSyncExternalStore(subscribe, getLocation)
}

export function navigate(to: string, opts: { replace?: boolean; state?: NavState } = {}): void {
  const st = opts.state ?? null
  const same = to === current.pathname + current.search + current.hash
  if (opts.replace || same) window.history.replaceState(st, '', to)
  else window.history.pushState(st, '', to)
  emit()
}

/** Обновить параметры строки запроса текущей страницы (без новой записи в истории). */
export function setSearchParams(patch: Record<string, string | null>): void {
  const sp = new URLSearchParams(current.search)
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === '') sp.delete(k)
    else sp.set(k, v)
  }
  const s = sp.toString()
  window.history.replaceState(window.history.state, '', current.pathname + (s ? `?${s}` : ''))
  emit()
}

export function useSearchParam(name: string): string | null {
  const loc = useLocation()
  return new URLSearchParams(loc.search).get(name)
}

/** Сопоставление шаблона вида /agents/:id с путём. */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean)
  const s = path.split('/').filter(Boolean)
  if (p.length !== s.length) return null
  const params: Record<string, string> = {}
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!
    const val = s[i]!
    if (seg.startsWith(':')) params[seg.slice(1)] = decodeURIComponent(val)
    else if (seg !== val) return null
  }
  return params
}

const TASK_RE = /^\/tasks\/\d+\/?$/

export function isTaskPath(path: string): boolean {
  return TASK_RE.test(path)
}

/** Открыть шторку задачи поверх текущей страницы. */
export function openTask(id: number): void {
  const loc = current
  const bg = isTaskPath(loc.pathname) ? (loc.state?.bg ?? '/') : loc.pathname + loc.search
  navigate(`/tasks/${id}`, { state: { bg } })
}

export function closeTask(): void {
  const bg = current.state?.bg
  navigate(bg && !isTaskPath(bg) ? bg : '/')
}

function isPlainClick(e: MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { to: string; children: ReactNode }

export function Link({ to, onClick, children, ...rest }: LinkProps) {
  return (
    <a
      href={to}
      onClick={(e) => {
        onClick?.(e)
        if (e.defaultPrevented || !isPlainClick(e)) return
        e.preventDefault()
        navigate(to)
      }}
      {...rest}
    >
      {children}
    </a>
  )
}

type TaskLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'id'> & { id: number; children: ReactNode }

export function TaskLink({ id, onClick, children, ...rest }: TaskLinkProps) {
  return (
    <a
      href={`/tasks/${id}`}
      onClick={(e) => {
        onClick?.(e)
        if (e.defaultPrevented || !isPlainClick(e)) return
        e.preventDefault()
        e.stopPropagation()
        openTask(id)
      }}
      {...rest}
    >
      {children}
    </a>
  )
}
